<template>
  <div id="total_project" class="project-summary-page">
    <CCard>
      <CCardHeader>
        <h2>
          <span>プロジェクト集計</span>
          <span class="card-header-actions">
            <router-link v-if="canOpenProjectRate" :to="projectRatePath">
              <button type="button" class="btn btn-outline-primary">
                金額設定
              </button>
            </router-link>
          </span>
        </h2>
      </CCardHeader>

      <CCardBody>
        <div class="project-summary-search">
          <div class="project-summary-search__field">
            <label for="project-summary-year">対象年月</label>
            <div class="project-summary-search__period">
              <select
                id="project-summary-year"
                class="custom-select"
                v-model="year"
                :disabled="loading"
              >
                <option
                  v-for="option in yearOption"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.name }}
                </option>
              </select>
              <select
                id="project-summary-month"
                class="custom-select"
                v-model="month"
                :disabled="loading"
              >
                <option
                  v-for="option in monthOption"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.name }}
                </option>
              </select>
              <span>業務分</span>
            </div>
          </div>

          <div class="project-summary-search__actions">
            <button
              type="button"
              class="btn btn-primary"
              :disabled="loading"
              @click="search(true)"
            >
              検索
            </button>
            <button
              v-if="canOpenSalesManagement"
              type="button"
              class="btn btn-outline-primary"
              :disabled="loading"
              @click="openSalesManagement"
            >
              請求書管理へ
            </button>
            <button
              type="button"
              class="btn btn-info"
              :disabled="loading || !callFlg || projects.length === 0"
              @click="openPdf"
            >
              PDF
            </button>
          </div>
        </div>
      </CCardBody>

      <template v-if="callFlg">
        <CCardBody class="project-summary-result">
          <div class="project-summary-period">
            <div>
              <p class="project-summary-period__eyebrow">PROJECT PERFORMANCE</p>
              <h3>{{ displayYearMonth }} プロジェクト別実績</h3>
            </div>
            <span class="project-summary-period__note">
              勤務表の日別プロジェクトから、同一アカウントの全員を自動集計します<br>
              請求書管理で新規登録すると、見込値から確定値へ切り替わります
            </span>
          </div>

          <div class="project-summary-kpis">
            <article class="project-summary-kpi">
              <span>プロジェクト</span>
              <strong>{{ summary.projectCount || 0 }}</strong>
              <small>件</small>
            </article>
            <article class="project-summary-kpi">
              <span>延べメンバー</span>
              <strong>{{ summary.memberCount || 0 }}</strong>
              <small>名</small>
            </article>
            <article class="project-summary-kpi">
              <span>総工数</span>
              <strong>{{ summary.totalWorkTime || '00:00' }}</strong>
              <small>h:m</small>
            </article>
            <article class="project-summary-kpi project-summary-kpi--money">
              <span>売上</span>
              <strong>{{ formatCurrency(summary.totalSales) }}</strong>
              <small>円</small>
            </article>
            <article class="project-summary-kpi project-summary-kpi--money">
              <span>原価</span>
              <strong>{{ formatCurrency(summary.totalCost) }}</strong>
              <small>円</small>
            </article>
            <article
              class="project-summary-kpi project-summary-kpi--profit"
              :class="{ 'project-summary-kpi--negative': toNumber(summary.grossProfit) < 0 }"
            >
              <span>粗利</span>
              <strong>{{ formatCurrency(summary.grossProfit) }}</strong>
              <small>{{ formatPercent(summary.grossMargin) }}</small>
            </article>
          </div>




          <section class="project-summary-fiscal-chart" aria-labelledby="project-summary-fiscal-title">
            <header class="project-summary-fiscal-chart__header">
              <div>
                <p>FISCAL YEAR TREND</p>
                <h4 id="project-summary-fiscal-title">{{ fiscalYearLabel }} 売上・原価・粗利推移</h4>
                <small>選択した対象月を含む4月〜翌年3月（3月決算）を表示します。</small>
              </div>
              <div class="project-summary-fiscal-chart__legend" aria-label="グラフ凡例">
                <span><i class="project-summary-fiscal-chart__legend-sales"></i>売上</span>
                <span><i class="project-summary-fiscal-chart__legend-cost"></i>原価</span>
                <span><i class="project-summary-fiscal-chart__legend-profit"></i>粗利</span>
              </div>
            </header>

            <div v-if="fiscalChartLoading" class="project-summary-fiscal-chart__loading">
              <CSpinner color="primary" size="sm" />
              年度推移を集計しています
            </div>

            <div v-else-if="fiscalChartError" class="alert alert-warning mb-0">
              年度推移を取得できませんでした。対象月の集計結果はそのまま利用できます。
            </div>

            <div v-else class="project-summary-fiscal-chart__scroll">
              <div class="project-summary-fiscal-chart__plot">
                <article
                  v-for="item in fiscalMonths"
                  :key="item.yearMonth"
                  class="project-summary-fiscal-chart__month"
                  :class="{ 'project-summary-fiscal-chart__month--selected': item.selected }"
                >
                  <div class="project-summary-fiscal-chart__bars">
                    <span
                      class="project-summary-fiscal-chart__bar project-summary-fiscal-chart__bar--sales"
                      :style="{ height: chartBarHeight(item.sales) }"
                      :title="`${item.label} 売上 ${formatCurrency(item.sales)}円`"
                    ></span>
                    <span
                      class="project-summary-fiscal-chart__bar project-summary-fiscal-chart__bar--cost"
                      :style="{ height: chartBarHeight(item.cost) }"
                      :title="`${item.label} 原価 ${formatCurrency(item.cost)}円`"
                    ></span>
                    <span
                      class="project-summary-fiscal-chart__bar project-summary-fiscal-chart__bar--profit"
                      :class="{ 'project-summary-fiscal-chart__bar--negative': toNumber(item.grossProfit) < 0 }"
                      :style="{ height: chartBarHeight(item.grossProfit) }"
                      :title="`${item.label} 粗利 ${formatCurrency(item.grossProfit)}円`"
                    ></span>
                  </div>
                  <strong>{{ item.shortLabel }}</strong>
                  <small>{{ formatCompactCurrency(item.sales) }}</small>
                  <em v-if="item.selected">表示月</em>
                </article>
              </div>
            </div>
          </section>

          <div
            v-if="summary.unsetRateCount > 0"
            class="alert alert-warning project-summary-alert"
          >
            金額未設定のメンバーが{{ summary.unsetRateCount }}件あります。
            <template v-if="canOpenProjectRate">「金額設定」から対象月の基本請求額・月額原価を登録してください。</template>
            <template v-else>「プロジェクトの削除・詳細設定」の権限を持つ担当者へ、基本請求額・月額原価の登録を依頼してください。</template>
          </div>

          <div v-if="projects.length === 0" class="preika-empty-state">
            <div>
              <strong>対象月のプロジェクトがありません</strong>
              <p>プロジェクト、所属メンバー、勤務実績を確認してください。</p>
            </div>
          </div>

          <div v-else class="project-summary-list">
            <article
              v-for="project in pagedProjects"
              :key="project.projectId"
              class="project-summary-project"
            >
              <header class="project-summary-project__header">
                <div class="project-summary-project__identity">
                  <div class="project-summary-project__title-row">
                    <h4>{{ project.projectName }}</h4>
                    <!--
                      サーバーが返すのは取り決めの値で、画面へ出す言葉ではない。
                      **見せ方はここで決める。**statusLabel() が読み替える。
                    -->
                    <span
                      class="project-summary-status"
                      :class="statusClass(project.status)"
                    >
                      {{ statusLabel(project.status) }}
                    </span>
                  </div>
                  <p>
                    {{ project.companyName || '顧客未設定' }}
                    <span>責任者：{{ project.managerName || '未設定' }}</span>
                    <span v-if="project.baseTimeMin || project.baseTimeMax">
                      精算基準時間：{{ project.baseTimeMin || '-' }}〜{{ project.baseTimeMax || '-' }}h
                    </span>
                  </p>
                </div>

                <div class="project-summary-project__metrics">
                  <div>
                    <span>工数</span>
                    <strong>{{ project.totalWorkTime || '00:00' }}</strong>
                  </div>
                  <div>
                    <span>売上</span>
                    <strong>{{ formatCurrency(project.totalSales) }}円</strong>
                  </div>
                  <div>
                    <span>粗利</span>
                    <strong :class="{ 'text-danger': toNumber(project.grossProfit) < 0 }">
                      {{ formatCurrency(project.grossProfit) }}円
                    </strong>
                  </div>
                </div>
              </header>

              <div class="table-responsive project-summary-members">
                <table class="table table-bordered">
                  <thead>
                    <tr>
                      <th>メンバー</th>
                      <th class="text-right">工数</th>
                      <th class="text-right">時間外</th>
                      <th class="text-right">休日</th>
                      <th class="text-right">深夜</th>
                      <th class="text-right">基本請求額</th>
                      <th class="text-right">月額原価</th>
                      <th class="text-right">精算</th>
                      <th class="text-right">売上</th>
                      <th class="text-right">原価</th>
                      <th class="text-right">粗利</th>
                      <th>値区分</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!project.members || project.members.length === 0">
                      <td colspan="12" class="text-center text-muted">
                        対象メンバーが設定されていません。
                      </td>
                    </tr>
                    <tr
                      v-for="member in project.members"
                      :key="project.projectId + '-' + member.userId"
                    >
                      <td>
                        <strong>{{ member.userName }}</strong>
                        <small
                          v-if="member.excluded"
                          class="project-summary-member-warning"
                        >
                          当月対象外（実績あり）
                        </small>
                        <small
                          v-else-if="!member.rateConfigured && !member.confirmed"
                          class="project-summary-member-warning"
                        >
                          金額未設定
                        </small>
                        <small
                          v-else-if="member.inheritedRate && !member.confirmed"
                          class="project-summary-member-warning"
                        >
                          継続金額
                        </small>
                      </td>
                      <td class="text-right">{{ member.workTime }}</td>
                      <td class="text-right">{{ member.overtimeTime }}</td>
                      <td class="text-right">{{ member.holidayTime }}</td>
                      <td class="text-right">{{ member.nightTime }}</td>
                      <td class="text-right">{{ formatCurrency(member.billingUnitPrice) }}</td>
                      <td class="text-right">{{ formatCurrency(member.costUnitPrice) }}</td>
                      <td class="text-right">{{ formatSettlement(member.settlementHours) }}</td>
                      <td class="text-right">{{ formatCurrency(member.salesAmount) }}</td>
                      <td class="text-right">{{ formatCurrency(member.costAmount) }}</td>
                      <td
                        class="text-right font-weight-bold"
                        :class="{ 'text-danger': toNumber(member.grossProfit) < 0 }"
                      >
                        {{ formatCurrency(member.grossProfit) }}
                      </td>
                      <td>
                        <span
                          class="project-summary-value-type"
                          :class="member.confirmed
                            ? 'project-summary-value-type--confirmed'
                            : 'project-summary-value-type--forecast'"
                        >
                          {{ member.confirmed
                            ? '確定'
                            : (member.inheritedRate ? '見込・継続' : '見込') }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <th>プロジェクト合計</th>
                      <th class="text-right">{{ project.totalWorkTime }}</th>
                      <th colspan="6"></th>
                      <th class="text-right">{{ formatCurrency(project.totalSales) }}</th>
                      <th class="text-right">{{ formatCurrency(project.totalCost) }}</th>
                      <th class="text-right">{{ formatCurrency(project.grossProfit) }}</th>
                      <th>{{ formatPercent(project.grossMargin) }}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </article>
          </div>

          <div v-if="totalPages > 1" class="d-flex justify-content-center mt-4">
            <VPagination
              v-model="currentPage"
              :click-handler="changePage"
              :page-count="totalPages"
              :force-page="currentPage"
              :force-ellipses="true"
              :container-class="'pagination'"
              :page-class="'page-item'"
              :prev-text="'<'"
              :next-text="'>'"
              :prev-link-class="'page-link'"
              :next-link-class="'page-link'"
              :page-link-class="'page-link'"
              :class-prefix="'pagination'"
            />
          </div>
        </CCardBody>
      </template>
    </CCard>

    <div v-if="loading" class="loading-overlay" aria-live="polite" aria-busy="true">
      <CSpinner color="primary" style="width: 3rem; height: 3rem;" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import VPagination from 'vuejs-paginate'
import ConstList from './../../ConstList'
import { requestFailureMessage } from '@/utils/requestFailure'
import { reportDownloadFailure } from '@/utils/downloadFailure'
import { getAccessContext } from '@/services/accessContext'
import { canAccessPath } from '@/utils/accessPolicy'

/*
 * 上部のボタンの行き先。**押して飛ぶ先と、ボタンを出すかの判定に同じ経路を使う。**
 *
 * この画面は「月次・プロジェクト集計の閲覧」があれば開けるが、行き先の2画面は
 * それとは別の権限が要る。前は誰にでも出していたので、事務担当者が押すと、権限が無いという
 * 断りの画面（見出しは「この画面を開く権限がありません」）になった（2026-09-27）。判定は左メニューと
 * 同じ canAccessPath に行き先の経路を渡すだけにし、ここで権限の名前を書き写さない。
 */
const PROJECT_RATE_PATH = '/admin/project-rate'
const SALES_MANAGEMENT_PATH = '/admin/sales/salesManagement'

export default {
  name: 'TotalProject',

  components: {
    VPagination
  },

  data () {
    return {
      year: '',
      month: '',
      yearOption: [],
      monthOption: [],
      callFlg: false,
      /*
       * いま出している結果の月（最後に検索が返った月）。
       * 見出し・PDF・［請求書管理へ］・年度の図は、年月の選択欄ではなくこの月で動かす。
       * 選択欄だけ変えると、9月の結果の見出しが「10月」になり、PDF は10月のものが出ていた。
       */
      shownYear: '',
      shownMonth: '',
      loading: false,
      summary: {},
      projects: [],
      fiscalMonths: [],
      fiscalYearLabel: '',
      fiscalChartLoading: false,
      fiscalChartError: false,
      fiscalChartRequestId: 0,
      currentPage: 1,
      pageSize: 8,
      projectRatePath: PROJECT_RATE_PATH,
      /* 取れるまでは null。null のあいだは行き先のボタンを出さない。 */
      accessContext: null
    }
  },

  computed: {
    canOpenProjectRate () {
      return canAccessPath(this.accessContext, PROJECT_RATE_PATH)
    },

    canOpenSalesManagement () {
      return canAccessPath(this.accessContext, SALES_MANAGEMENT_PATH)
    },

    displayYearMonth () {
      return `${this.shownYear}年${this.shownMonth}月`
    },

    totalPages () {
      return Math.max(1, Math.ceil(this.projects.length / this.pageSize))
    },

    pagedProjects () {
      const start = (this.currentPage - 1) * this.pageSize
      return this.projects.slice(start, start + this.pageSize)
    },

    fiscalChartMaximum () {
      const values = []
      this.fiscalMonths.forEach(item => {
        values.push(Math.abs(this.toNumber(item.sales)))
        values.push(Math.abs(this.toNumber(item.cost)))
        values.push(Math.abs(this.toNumber(item.grossProfit)))
      })
      return Math.max(1, ...values)
    }
  },

  created () {
    this.yearOption = ConstList.YEAR_OPTION
    this.monthOption = ConstList.MONTH_OPTION
    /* 取れなくても集計は見られる。行き先のボタンが出ないだけ。 */
    getAccessContext()
      .then(context => { this.accessContext = context })
      .catch(() => { this.accessContext = null })
  },

  mounted () {
    const saved = sessionStorage.getItem('project-summary-search-params')
    if (saved) {
      const params = JSON.parse(saved)
      this.year = params.year
      this.month = params.month
      this.search(false)
      return
    }

    const target = moment().subtract(1, 'months')
    this.year = target.format('YYYY')
    this.month = target.format('M')
  },

  methods: {
    search (resetPage) {
      if (this.loading) return

      if (resetPage) {
        this.currentPage = 1
      }

      this.loading = true
      this.callFlg = false
      // 頼んだ月。応答が返ったとき、この月を「いま出している結果の月」にする。
      const year = this.year
      const month = this.month

      axios
        .get(process.env.VUE_APP_API_ORIGIN + '/admin/project-summary', {
          params: {
            year: year,
            month: month
          }
        })
        .then(response => {
          const data = response.data || {}
          this.shownYear = year
          this.shownMonth = month
          this.summary = data.summary || {}
          this.projects = Array.isArray(data.projects) ? data.projects : []
          this.callFlg = true
          this.loadFiscalChart()

          if (this.currentPage > this.totalPages) {
            this.currentPage = this.totalPages
          }

          sessionStorage.setItem(
            'project-summary-search-params',
            JSON.stringify({ year: year, month: month })
          )
        })
        .catch(error => {
          this.handleError(error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    buildFiscalMonths () {
      const selectedYear = Number(this.shownYear)
      const selectedMonth = Number(this.shownMonth)
      const fiscalStartYear = selectedMonth >= 4 ? selectedYear : selectedYear - 1
      const start = moment(`${fiscalStartYear}-04-01`, 'YYYY-MM-DD')
      const selectedYearMonth = moment([
        selectedYear,
        selectedMonth - 1,
        1
      ]).format('YYYYMM')

      this.fiscalYearLabel = `${fiscalStartYear}年度（${fiscalStartYear}年4月〜${fiscalStartYear + 1}年3月）`

      return Array.from({ length: 12 }, (_, index) => {
        const target = start.clone().add(index, 'months')
        const yearMonth = target.format('YYYYMM')
        return {
          year: target.format('YYYY'),
          month: target.format('M'),
          yearMonth,
          label: target.format('YYYY年M月'),
          shortLabel: target.format('M月'),
          selected: yearMonth === selectedYearMonth
        }
      })
    },

    loadFiscalChart () {
      const requestId = ++this.fiscalChartRequestId
      const months = this.buildFiscalMonths()
      this.fiscalChartLoading = true
      this.fiscalChartError = false
      this.fiscalMonths = months.map(item => ({
        ...item,
        sales: 0,
        cost: 0,
        grossProfit: 0
      }))

      Promise.all(months.map(item =>
        axios.get(process.env.VUE_APP_API_ORIGIN + '/admin/project-summary', {
          params: {
            year: item.year,
            month: item.month
          }
        })
      ))
        .then(responses => {
          if (requestId !== this.fiscalChartRequestId) return
          this.fiscalMonths = months.map((item, index) => {
            const data = (responses[index] && responses[index].data) || {}
            const monthlySummary = data.summary || {}
            return {
              ...item,
              sales: this.toNumber(monthlySummary.totalSales),
              cost: this.toNumber(monthlySummary.totalCost),
              grossProfit: this.toNumber(monthlySummary.grossProfit)
            }
          })
        })
        .catch(() => {
          if (requestId !== this.fiscalChartRequestId) return
          this.fiscalChartError = true
        })
        .finally(() => {
          if (requestId === this.fiscalChartRequestId) {
            this.fiscalChartLoading = false
          }
        })
    },

    chartBarHeight (value) {
      const number = Math.abs(this.toNumber(value))
      if (number === 0) return '0'
      return `${Math.max(4, (number / this.fiscalChartMaximum) * 100)}%`
    },

    formatCompactCurrency (value) {
      const number = this.toNumber(value)
      const absolute = Math.abs(number)
      if (absolute >= 100000000) return `${(number / 100000000).toFixed(1)}億円`
      if (absolute >= 10000) return `${(number / 10000).toFixed(0)}万円`
      return `${Math.round(number).toLocaleString('ja-JP')}円`
    },

    openSalesManagement () {
      sessionStorage.setItem(
        'sales-search-params',
        // 結果が出ているときは、その結果の月の請求書管理を開く。検索する前は選択欄の月。
        JSON.stringify(this.callFlg
          ? { year: this.shownYear, month: this.shownMonth }
          : { year: this.year, month: this.month })
      )
      this.$router.push(SALES_MANAGEMENT_PATH)
    },

    openPdf () {
      if (this.loading || this.projects.length === 0) return

      this.loading = true
      axios
        .get(process.env.VUE_APP_API_ORIGIN + '/admin/project-summary/pdf', {
          params: {
            // 出している結果の月の帳票（選択欄の月ではない）。
            year: this.shownYear,
            month: this.shownMonth
          },
          responseType: 'blob'
        })
        .then(response => {
          const blob = new Blob([response.data], { type: 'application/pdf' })
          const url = URL.createObjectURL(blob)
          window.open(url, '_blank')
          window.setTimeout(() => URL.revokeObjectURL(url), 60000)
        })
        .catch((error) => {
          // 記録のPromiseはチェーンへ返さない。返すと finally が記録POSTの
          // 完了（あるいはタイムアウト）まで待ち、そのあいだボタンが
          // 押せないままになる。案内は reportDownloadFailure が先に出す。
          reportDownloadFailure(axios, error, 'PROJECT_TOTAL_DOWNLOAD_FAILED')
        })
        .finally(() => {
          this.loading = false
        })
    },

    changePage (page) {
      this.currentPage = page
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    /*
     * サーバーが返す状態の値を、画面へ出す言葉へ読み替える。
     *
     * **値と見せ方を分ける。**ルータの name（遷移の識別子）と
     * pageTitle（見出し）を分けているのと同じ考え方。
     *
     * 2026-09-21 に、画面の言葉を「単価」から「金額」へ変えたとき、
     * サーバーの返り値まで一緒に変えてしまい、バッジの色が消えた。
     * 直したあと、外部監査から次の指摘を受けて、値のほうは戻した。
     *
     *   backとfrontを同時に配置しても、既に開いているブラウザが
     *   同時に新frontへ切り替わるわけではありません。
     *
     * 値を変えると、その期間だけ旧画面のバッジが無色になる。
     */
    statusLabel (status) {
      return status === '単価未設定' ? '金額未設定' : status
    },

    statusClass (status) {
      switch (status) {
        case '売上登録済':
          return 'project-summary-status--confirmed'
        case '一部売上登録済':
          return 'project-summary-status--partial'
        case '集計可能':
          return 'project-summary-status--ready'
        /*
         * 新旧どちらの値でも警告にする。配備の切り替え中は、
         * 新しい画面が古いサーバーへ問い合わせることもある。
         */
        case '単価未設定':
        case '金額未設定':
          return 'project-summary-status--warning'
        case '工数未登録':
        case 'メンバー未設定':
          return 'project-summary-status--muted'
        default:
          return ''
      }
    },

    formatCurrency (value) {
      const number = this.toNumber(value)
      return Math.round(number).toLocaleString('ja-JP')
    },

    formatPercent (value) {
      return `${this.toNumber(value).toFixed(1)}%`
    },

    formatSettlement (value) {
      const number = this.toNumber(value)
      if (number > 0) return `+${number.toFixed(2)}h`
      if (number < 0) return `${number.toFixed(2)}h`
      return '0.00h'
    },

    toNumber (value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },

    handleError (error) {
      axios
        .post(
          `${process.env.VUE_APP_API_ORIGIN}/error/frontError`,
          { error: String(error) },
          { headers: { 'Content-Type': 'application/json' } }
        )
        .then(() => alert(requestFailureMessage(error)))
        .catch(() => alert(requestFailureMessage(error, ConstList.SYSTEM_COMMUNICATION_ERROR)))
    }
  }
}
</script>

<style scoped>
.project-summary-search {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
}

.project-summary-search__period,
.project-summary-search__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.project-summary-search__period .custom-select {
  width: 100px;
}

.project-summary-search__period .custom-select:nth-child(2) {
  width: 78px;
}

.project-summary-result {
  border-top: 1px solid var(--preika-border);
}

.project-summary-period {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}

.project-summary-period__eyebrow {
  margin: 0 0 4px;
  color: var(--preika-blue-600);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .15em;
}

.project-summary-period h3 {
  margin: 0;
  color: var(--preika-navy-900);
  font-size: 22px;
}

.project-summary-period__note {
  color: var(--preika-text-muted);
  font-size: 12px;
}

.project-summary-kpis {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.project-summary-kpi {
  position: relative;
  min-width: 0;
  padding: 16px;
  border: 1px solid var(--preika-border);
  border-radius: 14px;
  background: #fff;
  box-shadow: var(--preika-shadow-sm);
}

.project-summary-kpi span,
.project-summary-kpi strong,
.project-summary-kpi small {
  display: block;
}

.project-summary-kpi span {
  margin-bottom: 8px;
  color: var(--preika-text-muted);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .08em;
}

.project-summary-kpi strong {
  overflow: hidden;
  color: var(--preika-navy-900);
  font-size: 21px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-summary-kpi small {
  margin-top: 4px;
  color: var(--preika-text-muted);
  font-size: 12px;
}

.project-summary-kpi--profit {
  border-color: #bee7d5;
  background: var(--preika-success-100);
}

.project-summary-kpi--negative {
  border-color: #f2cbd0;
  background: var(--preika-danger-100);
}

.project-summary-fiscal-chart {
  margin-bottom: 20px;
  padding: 20px;
  border: 1px solid var(--preika-border);
  border-radius: 16px;
  background: linear-gradient(180deg, #fff, #f7fafc);
  box-shadow: var(--preika-shadow-sm);
}

.project-summary-fiscal-chart__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
}

.project-summary-fiscal-chart__header p {
  margin: 0 0 4px;
  color: var(--preika-blue-600);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .14em;
}

.project-summary-fiscal-chart__header h4 {
  margin: 0;
  color: var(--preika-navy-900);
  font-size: 16px;
}

.project-summary-fiscal-chart__header small {
  display: block;
  margin-top: 5px;
  color: var(--preika-text-muted);
  font-size: 12px;
}

.project-summary-fiscal-chart__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  color: var(--preika-text-muted);
  font-size: 12px;
  font-weight: 700;
}

.project-summary-fiscal-chart__legend span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.project-summary-fiscal-chart__legend i {
  width: 9px;
  height: 9px;
  border-radius: 3px;
}

.project-summary-fiscal-chart__legend-sales,
.project-summary-fiscal-chart__bar--sales {
  background: var(--preika-blue-600);
}

.project-summary-fiscal-chart__legend-cost,
.project-summary-fiscal-chart__bar--cost {
  background: var(--preika-warning-600);
}

.project-summary-fiscal-chart__legend-profit,
.project-summary-fiscal-chart__bar--profit {
  background: var(--preika-success-600);
}

.project-summary-fiscal-chart__loading {
  display: flex;
  min-height: 230px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--preika-text-muted);
  font-size: 12px;
}

.project-summary-fiscal-chart__scroll {
  overflow-x: auto;
  padding: 8px 2px 2px;
}

.project-summary-fiscal-chart__plot {
  display: grid;
  min-width: 820px;
  grid-template-columns: repeat(12, minmax(54px, 1fr));
  /* 月の下の金額（12px）が「…」で切れないよう、月と月のあいだを詰めて字の入る幅を取る。 */
  gap: 4px;
  align-items: end;
}

.project-summary-fiscal-chart__month {
  position: relative;
  min-width: 0;
  padding: 8px 2px 7px;
  border: 1px solid transparent;
  border-radius: 10px;
  text-align: center;
}

.project-summary-fiscal-chart__month--selected {
  border-color: var(--preika-blue-300);
  background: var(--preika-blue-050);
}

.project-summary-fiscal-chart__bars {
  display: flex;
  height: 180px;
  align-items: flex-end;
  justify-content: center;
  gap: 4px;
  padding: 0 3px;
  border-bottom: 1px solid var(--preika-gray-200);
}

.project-summary-fiscal-chart__bar {
  display: block;
  width: 11px;
  min-height: 0;
  border-radius: 5px 5px 2px 2px;
  opacity: .92;
  transition: height .2s ease;
}

.project-summary-fiscal-chart__bar--negative {
  background: var(--preika-danger-600);
}

.project-summary-fiscal-chart__month strong,
.project-summary-fiscal-chart__month small,
.project-summary-fiscal-chart__month em {
  display: block;
}

.project-summary-fiscal-chart__month strong {
  margin-top: 7px;
  color: var(--preika-navy-900);
  font-size: 12px;
}

.project-summary-fiscal-chart__month small {
  margin-top: 2px;
  overflow: hidden;
  color: var(--preika-text-muted);
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-summary-fiscal-chart__month em {
  margin-top: 3px;
  color: var(--preika-blue-700);
  font-size: 12px;
  font-style: normal;
  font-weight: 800;
}

.project-summary-alert {
  margin-bottom: 18px;
}

.project-summary-list {
  display: grid;
  gap: 18px;
}

.project-summary-project {
  overflow: hidden;
  border: 1px solid var(--preika-border);
  border-radius: 16px;
  background: #fff;
  box-shadow: var(--preika-shadow-sm);
}

.project-summary-project__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 18px 20px;
  border-bottom: 1px solid var(--preika-gray-150);
  background: linear-gradient(180deg, #fff, #f9fbfc);
}

.project-summary-project__identity {
  min-width: 0;
  flex: 1;
}

.project-summary-project__title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.project-summary-project h4 {
  margin: 0;
  color: var(--preika-navy-900);
  font-size: 16px;
}

.project-summary-project__identity p {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 18px;
  margin: 7px 0 0;
  color: var(--preika-text-muted);
  font-size: 12px;
}

.project-summary-project__metrics {
  display: flex;
  align-items: center;
  gap: 20px;
}

.project-summary-project__metrics div {
  min-width: 80px;
  text-align: right;
}

.project-summary-project__metrics span,
.project-summary-project__metrics strong {
  display: block;
}

.project-summary-project__metrics span {
  color: var(--preika-text-muted);
  font-size: 12px;
  font-weight: 800;
}

.project-summary-project__metrics strong {
  margin-top: 3px;
  color: var(--preika-navy-900);
  font-size: 12px;
}

.project-summary-status,
.project-summary-value-type {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.project-summary-status--confirmed,
.project-summary-value-type--confirmed {
  background: var(--preika-success-100);
  color: var(--preika-success-700);
}

.project-summary-status--ready,
.project-summary-value-type--forecast {
  background: var(--preika-blue-100);
  color: var(--preika-blue-800);
}

.project-summary-status--partial {
  background: #eee9fb;
  color: #6650b7;
}

.project-summary-status--warning {
  background: var(--preika-warning-100);
  color: var(--preika-warning-700);
}

.project-summary-status--muted {
  background: var(--preika-gray-100);
  color: var(--preika-gray-600);
}

.project-summary-members {
  border: 0;
  border-radius: 0;
}

.project-summary-members .table {
  margin: 0;
}

.project-summary-members tfoot th {
  background: var(--preika-gray-050);
}

.project-summary-member-warning {
  display: block;
  margin-top: 3px;
  color: var(--preika-warning-700);
  font-size: 12px;
}

/*
  6枚のカード（売上・原価・粗利など）を1段に並べるのは 1280px から。金額は省略記号で切る作りなので、
  1200〜1279px の6列では末尾の数字が消えていた（2026-09-26 の撮影）。
*/
@media (max-width: 1279.98px) {
  .project-summary-kpis {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1199.98px) {

  .project-summary-project__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .project-summary-project__metrics {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 767.98px) {
  .project-summary-fiscal-chart__header {
    flex-direction: column;
  }

  .project-summary-search,
  .project-summary-period {
    align-items: stretch;
    flex-direction: column;
  }

  .project-summary-search__actions .btn {
    flex: 1;
  }

  .project-summary-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .project-summary-project__metrics {
    justify-content: space-between;
    gap: 8px;
  }

  .project-summary-project__metrics div {
    min-width: 0;
    text-align: left;
  }
}
</style>
